import React, { useState } from 'react';
import { MultiplayerState } from '../types';
import { Language, t } from '../data/translations';
import tavernBg from '../assets/ui/fantasy/tavern_bg.png';
import woodPanel from '../assets/ui/fantasy/wood_panel.png';
import btnWood from '../assets/ui/fantasy/btn_wood.png';

interface CreateRoomScreenProps {
  onCreateRoom: (playerName: string, roomName: string, password?: string) => void;
  onBack: () => void;
  defaultPlayerName: string;
  multiplayerState: MultiplayerState;
  wsError: string | null;
  language: Language;
}

const CreateRoomScreen: React.FC<CreateRoomScreenProps> = ({ onCreateRoom, onBack, defaultPlayerName, multiplayerState, wsError, language }) => {
  const [roomName, setRoomName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [password, setPassword] = useState('');

  const isFormValid = roomName.trim().length >= 1 && (!isPrivate || password.length > 0);
  const { connectionStatus } = multiplayerState;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid && connectionStatus === 'CONNECTED') {
      onCreateRoom(defaultPlayerName, roomName.trim(), isPrivate ? password : undefined);
    }
  };

  const commonButtonStyles: React.CSSProperties = {
    borderStyle: 'solid',
    borderWidth: '10px',
    borderImageSource: `url(${btnWood})`,
    borderImageSlice: '30 fill',
    textShadow: '1px 1px 2px black'
  };

  return (
    <div 
      className="w-full h-screen flex flex-col items-center justify-center animate-[fadeIn_0.5s_ease-out] bg-cover bg-center font-['Crimson_Text']"
      style={{ backgroundImage: `url(${tavernBg})` }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      <div className="relative z-10 flex flex-col items-center w-full max-w-lg">
        <h2 className="text-5xl font-['Cinzel'] font-black text-transparent bg-clip-text bg-gradient-to-b from-[#FFD700] via-[#FDB931] to-[#996515] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] mb-8">
          {t('multiplayer.createRoom', language)}
        </h2>

        <form 
          onSubmit={handleSubmit} 
          className="flex flex-col gap-6 w-full p-10 rounded-xl shadow-2xl border-[6px] border-[#3E2723] outline outline-2 outline-[#DAA520]"
          style={{ backgroundImage: `url(${woodPanel})`, backgroundSize: 'cover' }}
        >
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/40 rounded-lg pointer-events-none"></div>

          <div className="relative z-10">
            <label htmlFor="roomName" className="block text-lg font-['Cinzel'] font-bold text-amber-200 mb-2">{t('multiplayer.roomName', language)}</label>
            <input
              id="roomName"
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder={t('multiplayer.roomNamePlaceholder', language)}
              className="w-full bg-black/60 border-2 border-[#8B4513] rounded-lg px-4 py-3 text-amber-100 focus:outline-none focus:border-[#DAA520] transition-colors placeholder-amber-900/50 text-lg shadow-inner"
            />
          </div>

          <div className="relative z-10 flex items-center gap-4 py-2">
            <input
              id="isPrivate"
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="w-6 h-6 rounded bg-black/60 border-2 border-[#8B4513] text-[#DAA520] focus:ring-[#DAA520] focus:ring-offset-0 cursor-pointer"
            />
            <label htmlFor="isPrivate" className="text-lg font-['Cinzel'] font-bold text-amber-100 cursor-pointer select-none">{t('multiplayer.privateRoom', language)}</label>
          </div>

          {isPrivate && (
            <div className="relative z-10 animate-[fadeIn_0.3s_ease-out]">
              <label htmlFor="password" className="block text-lg font-['Cinzel'] font-bold text-amber-200 mb-2">{t('multiplayer.setPassword', language)}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('multiplayer.passwordPlaceholder', language)}
                className="w-full bg-black/60 border-2 border-[#8B4513] rounded-lg px-4 py-3 text-amber-100 focus:outline-none focus:border-[#DAA520] transition-colors placeholder-amber-900/50 text-lg shadow-inner"
              />
            </div>
          )}

          {wsError && <p className="relative z-10 text-red-400 text-sm text-center mt-2 font-bold drop-shadow-md">{wsError}</p>}
          {connectionStatus !== 'CONNECTED' && (
            <p className="relative z-10 text-yellow-400 text-sm text-center mt-2 font-bold drop-shadow-md animate-pulse">
              {connectionStatus === 'CONNECTING' ? t('multiplayer.connecting', language) : t('multiplayer.disconnected', language)}
            </p>
          )}

          <div className="relative z-10 flex flex-col gap-4 mt-6">
            <button
              type="submit"
              disabled={!isFormValid || connectionStatus !== 'CONNECTED'}
              className="transition-all transform hover:scale-105 hover:brightness-110 text-amber-100 font-['Cinzel'] font-bold text-xl py-2 px-6 shadow-2xl disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed disabled:transform-none"
              style={commonButtonStyles}
            >
              {t('multiplayer.create', language)}
            </button>
            <button
              type="button"
              onClick={onBack}
              className="transition-all transform hover:scale-105 hover:brightness-110 text-amber-200/70 font-['Cinzel'] font-bold text-lg py-1 px-6 shadow-xl opacity-90"
              style={commonButtonStyles}
            >
              {t('multiplayer.back', language)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRoomScreen;
